import React, { forwardRef } from "react"
import posed from "react-pose"
import styled from "styled-components"
import PropTypes from "prop-types"

import theme from "~/style/theme"

const BaseAnimation = posed.button({
  init: {
    borderColor: props =>
      props.error ? theme.colors.error : theme.colors.ui.inputBorder,
    color: props =>
      props.error ? theme.colors.error : theme.colors.text.input,
    boxShadow: "0 7px 13px 0 rgba(35,41,48,0)",
    scale: 1,
    background: "rgba(255,255,255,0)"
  },
  error: {
    color: theme.colors.error,
    borderColor: theme.colors.error
  },
  drop: {
    color: theme.colors.success,
    borderColor: theme.colors.success
  },
  hoverable: true,
  hover: {
    delay: 0,
    background: "rgba(255,255,255,1)",
    boxShadow: "0 7px 13px 0 rgba(35,41,48,0.15)",
    color: props => (props.error ? theme.colors.error : "#262626"),
    borderColor: "rgba(255,255,255,0)",
    scale: 1
  },
  focusable: true,
  focus: {
    delay: 0,
    background: "#FFFFFF",
    boxShadow: "0 7px 13px 0 rgba(35,41,48,0.15)",
    color: props => (props.error ? theme.colors.error : "#262626"),
    borderColor: "rgba(255,255,255,0)",
    scale: 1
  },
  pressable: true,
  press: {
    scale: 0.95,
    boxShadow: "0 0px 0px 0 rgba(35,41,48,0.15)"
  }
})

const GrayButtonContainer = styled(BaseAnimation)`
  border-radius: 4px;
  border: solid 1px;
  padding: ${props => (props.hasText ? "14px" : "14px 8px")};
  display: inline-flex;
  flex-grow: 0;
  align-items: center;
  width: ${props => (props.fullWidth ? "100%" : "auto")};
  height: ${props => (props.fullHeight ? "100%" : "auto")};
  color: ${props => props.color};
  font-family: ${props => props.theme.fonts.avenirNextRegular};
  font-size: 0.79rem;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  margin: {
    props=>props.nomargin?null : "0 0 5px 0";
  }
  cursor: pointer;
  outline: none;
  flex-direction: ${props => (props.column ? "column" : "row")};
`

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;
  margin-right: ${props => (props.hasText && !props.column ? "14px" : "0px")};
`

const ChildContainer = styled.div`
  display: flex;
  justify-content: ${props => (props.fullWidth ? "flex-start" : "center")};
  align-items: center;
  flex-grow: 1;
`

const GrayButton = forwardRef(({
  Svg,
  image,
  alt,
  children,
  clickable,
  error,
  dragActive,
  onClick,
  fullWidth,
  fullHeight,
  column,
  noMargin
}, ref) => {
  let pose = null
  if (error) pose = "error"
  if (dragActive) pose = "drop"

  let color = theme.colors.text.input
  if (error) {
    color = theme.colors.error
  }
  if (dragActive) {
    color = theme.colors.success
  }
  return (
    <GrayButtonContainer
      ref={ref}
      onClick={onClick}
      clickable={clickable}
      hasText={!!children}
      pose={pose}
      error={error}
      fullWidth={fullWidth}
      fullHeight={fullHeight}
      column={column}
      no={noMargin}
    >
      {image ? (
        <IconContainer hasText={!!children} column={column}>
          <img src={image} alt={alt} />
        </IconContainer>
      ) : null}
      {Svg ? (
        <IconContainer hasText={!!children} column={column}>
          <Svg color={color} />
        </IconContainer>
      ) : null}
      {children ? (
        <ChildContainer fullWidth={fullWidth} fullHeight={fullHeight}>
          <div>{children}</div>
        </ChildContainer>
      ) : null}
    </GrayButtonContainer>
  )
})

GrayButton.defaultProps = {
  image: null,
  Svg: null,
  alt: null,
  clickable: false,
  error: false,
  dragActive: false,
  onClick: () => {}
}

GrayButton.propTypes = {
  // eslint-disable-next-line
  Svg: PropTypes.any,
  dragActive: PropTypes.bool,
  image: PropTypes.string,
  alt: PropTypes.string,
  clickable: PropTypes.bool,
  error: PropTypes.bool,
  onClick: PropTypes.func
}

export default GrayButton
